import { useState, useRef, useEffect } from 'react';
import { IconChevron } from './icons';
import {
  createDigest,
  deleteDigest,
  updateDigest,
  type Digest,
  type DigestCadenceOption,
} from '../repositories/digestRepository';

/**
 * The one control for subscribing a saved search to an email digest, shared by
 * the feed list and the digests page so both do the same thing -- including
 * the part that is easy to get wrong separately: switching cadence on an
 * existing subscription is a PATCH, not a cancel and a re-subscribe, because
 * re-subscribing would keep the ledger but a cancel/create round trip can
 * leave the row cancelled if the second call fails.
 *
 * Length is not offered. It follows from the cadence (the backend derives it),
 * and the menu shows what that means rather than asking.
 */
interface DigestSubscriptionMenuProps {
  descriptionId: string;
  /** The live subscription for this saved search, if there is one. */
  digest?: Digest;
  cadences: DigestCadenceOption[];
  /** Called after any successful change, so the owner can re-read its list. */
  onChanged: () => void | Promise<void>;
  onError: (message: string) => void;
}

const NO_SUBSCRIPTION = 'Subscribe by email';

export function DigestSubscriptionMenu({
  descriptionId,
  digest,
  cadences,
  onChanged,
  onError,
}: DigestSubscriptionMenuProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  // Close on an outside click, so the menu behaves like the other dropdowns
  // rather than staying open until something else is clicked inside it.
  useEffect(() => {
    if (!open) return;
    const onDocumentClick = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [open]);

  const current = digest
    ? cadences.find((c) => c.value === digest.cadence)?.label ?? digest.cadence
    : NO_SUBSCRIPTION;

  const choose = async (cadence: string | null) => {
    setOpen(false);
    if (busy) return;
    if (cadence === (digest?.cadence ?? null)) return;
    setBusy(true);
    try {
      if (cadence === null) {
        if (digest) await deleteDigest(digest.id);
      } else if (digest) {
        await updateDigest(digest.id, { cadence });
      } else {
        await createDigest({ description_id: descriptionId, cadence });
      }
      await onChanged();
    } catch (err) {
      onError((err as Error).message || 'Could not change your email subscription');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="digest-menu" ref={container}>
      <button
        type="button"
        className={`digest-menu-trigger${digest ? ' digest-menu-trigger-on' : ''}`}
        onClick={() => setOpen((v) => !v)}
        disabled={busy || cadences.length === 0}
        title="Email digests are an experiment"
      >
        {current}
        <span className="digest-menu-beta" aria-label="beta">🧪</span>
        <IconChevron open={open} />
      </button>
      {open && (
        <div className="search-pill-dropdown search-pill-dropdown-right">
          <button
            type="button"
            className="search-pill-dropdown-item"
            onClick={() => void choose(null)}
          >
            No subscription
          </button>
          {cadences.map((cadence) => (
            <button
              key={cadence.value}
              type="button"
              className="search-pill-dropdown-item"
              onClick={() => void choose(cadence.value)}
            >
              {cadence.label}
              <span className="digest-menu-item-note">
                {cadence.article_count} stories
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
